import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import withHeightAuto from '@activebrands/core-web/components/withHeightAuto';
import media from '@activebrands/core-web/config/media';
import { styled } from '@activebrands/core-web/libs/styletron';
import ActRewardIcon from 'components/icons/ActRewardIcon';
import Heading from 'components/text/Heading';
import UnAuthenticatedContent from '../LoginSection/UnAuthenticatedContent';
import AuthenticatedActRewardsContent from './AuthenticatedActRewardsContent';
import UnAuthenticatedActRewardsContent from './UnAuthenticatedActRewardsContent';

const ACT_REWARDS_ENABLED = process.env.REACT_APP_ENABLE_ACT_REWARDS === 'true';
const ACT_REWARDS_PRICELIST = process.env.REACT_APP_ACT_REWARDS_PRICELIST;

const Wrapper = styled('div', {
    padding: '12px',
    backgroundColor: 'var(--color-bg-checkout-act-content)',
    transition: 'all var(--duration-primary) var(--ease)',

    [media.min['desktop.sm']]: {
        marginLeft: 'var(--margin-content)',
        marginRight: 'var(--margin-content)',
    },
});

const Header = styled('div', {
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    transition: 'inherit',
});

const Tag = styled('p', {
    display: 'inline-block',
    float: 'right',
    color: 'var(--color-text-subtle)',
    transition: 'inherit',
    ...textStyles['Regular-12/100'],
});

const Collapse = withHeightAuto(
    styled('div', {
        position: 'relative',
        height: '0', // Will work as default height
        overflow: 'hidden',
        transition: 'inherit',
    })
);

const ActRewardsSection = ({ $style, actRewardsHeading, checkoutCurrentStep, checkoutStep, loginHeading, ...rest }) => {
    const { user, isAuthenticated } = useSelector(state => state.auth);
    const [isOpen, setIsOpen] = useState(true);
    const pricelistId = useSelector(state => state.application.shopConfig.pricelistId);
    const actRewardsPricelist = ACT_REWARDS_PRICELIST === pricelistId;

    useEffect(() => {
        if (checkoutCurrentStep === checkoutStep) {
            setIsOpen(true);
        } else if (isAuthenticated && checkoutCurrentStep > checkoutStep) {
            setIsOpen(false);
        }
    }, [checkoutStep, checkoutCurrentStep, isAuthenticated]);

    if (!isAuthenticated && checkoutCurrentStep > checkoutStep) {
        return null;
    }

    const renderContentStyling = { marginTop: '8px' };

    // render different content for customers on correctpricelist
    const renderCorrectContent = () => {
        if (isAuthenticated && actRewardsPricelist && ACT_REWARDS_ENABLED) {
            return <AuthenticatedActRewardsContent $style={renderContentStyling} {...rest} />;
        }
        if (!isAuthenticated && actRewardsPricelist) {
            return <UnAuthenticatedActRewardsContent $style={renderContentStyling} {...rest} />;
        }
        if (isAuthenticated && !actRewardsPricelist) {
            return null;
        }
        if (!isAuthenticated && !actRewardsPricelist) {
            return <UnAuthenticatedContent $style={renderContentStyling} {...rest} />;
        }
        return null;
    };

    // different heading if not on act rewards pricelist
    let headingString = loginHeading;
    if (!isAuthenticated && actRewardsPricelist && ACT_REWARDS_ENABLED) {
        headingString = actRewardsHeading;
    }
    if (isAuthenticated) {
        headingString = fm('Hi {name}!', { name: user?.firstname || user?.email });
    }

    return (
        <Wrapper $style={$style}>
            <Header>
                <Heading
                    $style={{ display: 'inline-flex', alignItems: 'center', margin: 0 }}
                    fontKeys="Secondary/16_100_-02"
                >
                    {isAuthenticated && !isOpen && ACT_REWARDS_ENABLED && (
                        <ActRewardIcon
                            $style={{ marginRight: '8px' }}
                            color="var(--color-icon-checkout-act-content)"
                            width="8px"
                        />
                    )}
                    {headingString}
                </Heading>
                {ACT_REWARDS_ENABLED && <Tag $style={{ opacity: isOpen ? '0' : '1' }}>{fm('ACT Rewards Member')}</Tag>}
            </Header>
            <Collapse height={isOpen ? 'auto' : 0}>{renderCorrectContent()}</Collapse>
        </Wrapper>
    );
};

ActRewardsSection.propTypes = {
    $style: PropTypes.object,
    actRewardsHeading: PropTypes.string,
    checkoutCurrentStep: PropTypes.number,
    checkoutStep: PropTypes.number,
    loginHeading: PropTypes.string,
};

export default ActRewardsSection;
